/*
 * Every measurement of the uploader lives here, in design pixels on a fixed 540 x 675 canvas.
 * The panel is drawn at this size and scaled as a whole to fit the screen, so physics,
 * layout and pointer math all share one coordinate system.
 */

export type Vec = {
    x: number
    y: number
}

export const PANEL = {
    width: 540,
    height: 675,
    padding: 28,
    radius: 28
} as const

// Header is 50px tall (title 28 + gap 4 + subtitle 18), then a 28px gap before the zone.
export const ZONE = {
    x: 28,
    y: 106,
    width: 484,
    height: 270,
    radius: 20
} as const

// The rim sits on the bottom edge of the zone and is 60% of the backboard's width.
export const RIM = {
    cx: 270,
    cy: 376,
    rx: 38,
    ry: 7
} as const

// 26% of the zone's width, 30% of its height, in the lower middle of the zone.
export const BACKBOARD = {
    x: 207,
    y: 279,
    width: 126,
    height: 81
} as const

export const BACKBOARD_CENTER: Vec = {
    x: BACKBOARD.x + BACKBOARD.width / 2,
    y: BACKBOARD.y + BACKBOARD.height / 2
}

// 28% of the zone's height, tapering to 55% of the rim's width at the bottom.
export const NET = {
    height: 76,
    taper: 0.55
} as const

export const FILE_SIZE = {
    width: 56,
    height: 72
} as const

// Resting spot of the demo file: its left edge 14% in, its bottom edge 12% above the panel's bottom.
export const REST: Vec = {
    x: 104,
    y: 558
}

// Upload rows stack upward from just above the Clear button.
export const ROWS = {
    bottom: 640,
    height: 64,
    gap: 10,
    visible: 2
} as const

// Where a made shot arrives: centred over the rim, its lower edge just touching the rim line.
export const ENTRY_Y = RIM.cy - 18

// The trail canvas reaches past the panel so high arcs keep their dots.
export const TRAIL_CANVAS = {
    left: -60,
    top: -160,
    width: 660,
    height: 875
} as const

// The demo file sits on top of the row stack instead of underneath it.
export const restPointFor = (rows: number): Vec => {
    const visible = Math.min(Math.max(rows, 0), ROWS.visible)

    if (visible === 0) return { x: REST.x, y: REST.y }

    const stackTop = ROWS.bottom - visible * ROWS.height - (visible - 1) * ROWS.gap

    return { x: REST.x, y: Math.min(REST.y, stackTop - 70) }
}
